import { useLayoutEffect } from 'react';
import { BordCheckBox } from 'bord-design-system';
import { BordOneToneIcon } from 'components/NudosComponents';
import { IlogisticServiceTrelloCardData } from '../../../../../../../types/services';
import SelectableToolCard from 'components/NewSelectableToolCard/SelectableToolCard';
import NudosDatePicker from 'components/NudosComponents/NudosDatePicker/NudosDatePicker';
import useDeliveredToolsState, { SECTIONS_STEPS } from '../../../../../../../state/useDeliveredToolsState';
import { LogisticsOriginAndDestinationInfo } from '../LogisticsOriginAndDestinationInfo/LogisticsOriginAndDestinationInfo';
import type { OffboardingLateralChrome } from '../../offboardingLateralModal.types';
import { bordColorTheme } from '../../../../../../../styles/styles.constants';

import './SelectedToolsSection.scss';

interface SelectedToolsSectionProps {
  closeModalCallback: () => void;
  cardData: IlogisticServiceTrelloCardData;
  isUpdateTools?: boolean;
  isLoading?: boolean;
  onLateralChromeChange?: (chrome: OffboardingLateralChrome | null) => void;
}

type FlowHeaderIconVariant = 'refreshCw' | 'truck';

interface FlowChromeContent {
  sectionTitle: string;
  sectionSubtitle?: string;
  headerIconVariant: FlowHeaderIconVariant;
  additionalToolsButton: { text: string };
  selectedToolsTitle: string;
  nextButton: {
    text: string;
    action: () => void;
    disabledCondition: () => boolean;
    isLoading?: boolean;
  };
  backButton: { text: string; action: () => void };
}

/**
 * Paso de selección de herramientas: cuerpo del panel lateral; cabecera y pie los define el padre vía callback.
 */
const SelectedToolsSection = ({
  closeModalCallback,
  cardData,
  isUpdateTools = false,
  isLoading = false,
  onLateralChromeChange
}: SelectedToolsSectionProps) => {
  const {
    deliveryDate,
    setDeliveryDate,
    setCurrentStep,
    availableTools,
    getSelectedToolsArray,
    getUnselectedToolsArray,
    selectAll,
    toggleSelectAll,
    toggleToolSelection
  } = useDeliveredToolsState();

  const selectedTools = getSelectedToolsArray();
  const unselectedTools = getUnselectedToolsArray();

  const handleGoToAddAdditionalTools = () => setCurrentStep(SECTIONS_STEPS.ADD_ADDITIONAL_TOOLS);
  const handleNextStep = () => setCurrentStep(SECTIONS_STEPS.CONFIRMATION);
  const onToolSelection = (index: number) => toggleToolSelection(index);
  const onSelectAll = () => toggleSelectAll();

  const getContent = (updateTools: boolean): FlowChromeContent => {
    if (updateTools) {
      return {
        sectionTitle: 'Actualizar entrega',
        sectionSubtitle: 'Confirma las herramientas que realmente serán entregadas',
        headerIconVariant: 'refreshCw',
        additionalToolsButton: {
          text: 'Llegaron otras herramientas'
        },
        selectedToolsTitle: 'Selecciona las herramientas que fueron entregadas en el servicio',
        nextButton: {
          text: 'Siguiente',
          action: handleNextStep,
          disabledCondition: () => false,
          isLoading
        },
        backButton: {
          text: 'Volver',
          action: closeModalCallback
        }
      };
    }

    return {
      sectionTitle: 'Pasar a entregado',
      sectionSubtitle: 'Registra la fecha y confirma las herramientas entregadas',
      headerIconVariant: 'truck',
      additionalToolsButton: {
        text: 'Llegaron otras herramientas'
      },
      selectedToolsTitle: 'Selecciona las herramientas que serán entregadas en el servicio',
      nextButton: {
        text: 'Siguiente',
        action: handleNextStep,
        disabledCondition: () => deliveryDate?.length === 0,
        isLoading
      },
      backButton: {
        text: 'Volver',
        action: closeModalCallback
      }
    };
  };

  const content = getContent(isUpdateTools);

  useLayoutEffect(() => {
    if (!onLateralChromeChange) {
      return undefined;
    }

    const chromeContent = getContent(isUpdateTools);
    const isNextDisabled = chromeContent.nextButton.disabledCondition() || !!chromeContent.nextButton.isLoading;

    onLateralChromeChange({
      headerProps: {
        title: chromeContent.sectionTitle,
        subtitle: chromeContent.sectionSubtitle,
        subtitleClassName: 'text-xss text-nudos-gray-500 font-normal',
        leading: (
          <span className="selectedToolsSectionHeaderLeading">
            <BordOneToneIcon
              variant={chromeContent.headerIconVariant}
              customWidth="2.4rem"
              stroke={bordColorTheme.stroke.default}
            />
          </span>
        ),
        showCloseButton: true,
        onClose: closeModalCallback
      },
      footerProps: {
        firstButton: {
          label: chromeContent.backButton.text,
          modeButton: 'secondary',
          customWidth: 'w-full',
          onClick: chromeContent.backButton.action
        },
        secondButton: {
          label: chromeContent.nextButton.text,
          modeButton: 'primary',
          customWidth: 'w-full',
          onClick: chromeContent.nextButton.action,
          disabled: isNextDisabled,
          isLoading: chromeContent.nextButton.isLoading
        }
      }
    });

    return undefined;
  }, [onLateralChromeChange, closeModalCallback, isUpdateTools, isLoading, deliveryDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const toolsCount = availableTools?.length ?? 0;
  const toolsSummaryText = `${toolsCount} herramientas por entregar`;

  return (
    <div className="selectedToolsSection">
      <LogisticsOriginAndDestinationInfo cardData={cardData} />
      
      <div className={isUpdateTools ? 'toolsMetaRow toolsMetaRow_singleLine' : 'toolsMetaRow toolsMetaRow_withDate'}>
        {!isUpdateTools && (
          <NudosDatePicker
            componentSize="small"
            label="Fecha de entrega (obligatorio)"
            updateCurrentValueCallback={setDeliveryDate}
            isFilled={!!deliveryDate}
            defaultValue={deliveryDate}
            desiredTimeZone={-6}
          />
        )}

        <div className={`toolsMetaRowSummary ${isUpdateTools ? 'text-end' : ''}`}>
          <p className="toolsActionText">{toolsSummaryText}</p>
          <button type="button" className="additionalToolsButton" onClick={handleGoToAddAdditionalTools}>
            {content.additionalToolsButton.text}
          </button>
        </div>
      </div>

      <hr className="selectedToolsSectionDivider" />

      <h2 className="selectedToolsTitle">{content.selectedToolsTitle}</h2>

      <div className="selectAllContainer">
        <BordCheckBox
          label="Seleccionar todas las herramientas"
          isButtonActive={selectAll}
          handleClickButton={onSelectAll}
          customSize="1.6rem"
        />
      </div>

      <div className="toolsList">
        {selectedTools?.map(({ tool, originalIndex }) => (
          <SelectableToolCard
            key={`selected-${originalIndex}`}
            tool={tool}
            isSelected
            showCheckbox
            onSelectionChange={() => onToolSelection(originalIndex)}
            customClassName="borderBlack selectedToolsSectionToolCard"
          />
        ))}

        {selectedTools?.length > 0 && unselectedTools?.length > 0 && <div className="toolsDivider" />}

        {unselectedTools?.map(({ tool, originalIndex }) => (
          <SelectableToolCard
            key={`unselected-${originalIndex}`}
            tool={tool}
            showCheckbox
            onSelectionChange={() => onToolSelection(originalIndex)}
            customClassName="borderGray selectedToolsSectionToolCard"
          />
        ))}
      </div>
    </div>
  );
};

export default SelectedToolsSection;
